// Anchors a Hear-it pin to a DOM element: a stable-ish CSS selector plus a text snippet fallback.
// Pins dropped on a Leaflet map also store lat/lng so they stay glued to that spot when the map pans or zooms.
import L from "leaflet";

if (!L.Map.__wallHooked) {
  L.Map.addInitHook(function () { this._container.__wallMap = this; });
  L.Map.__wallHooked = true;
}

export function mapFor(el) {
  const c = el?.closest?.(".leaflet-container") || el?.querySelector?.(".leaflet-container");
  return c?.__wallMap ? { c, map: c.__wallMap } : null;
}

const INLINE = new Set(["SPAN", "A", "B", "I", "EM", "STRONG", "SMALL", "SVG", "PATH", "BUTTON", "LABEL", "INPUT", "CODE", "SUP", "SUB", "MARK", "TIME", "USE", "G", "CIRCLE", "RECT", "LINE", "POLYGON", "BR"]);
const esc = (s) => (window.CSS?.escape ? window.CSS.escape(s) : s.replace(/["\\]/g, "\\$&"));

// Nearest block-ish ancestor of the tapped element (never the pin layer itself).
export function blockFor(target) {
  let el = target;
  while (el && el !== document.body && INLINE.has(el.tagName?.toUpperCase())) el = el.parentElement;
  return el && el !== document.documentElement ? el : document.body;
}

export function selectorFor(el) {
  const parts = [];
  let node = el;
  while (node && node !== document.body) {
    const id = node.id, tid = node.getAttribute("data-testid");
    if (id && document.querySelectorAll(`#${esc(id)}`).length === 1) { parts.unshift(`#${esc(id)}`); return parts.join(" > "); }
    if (tid && document.querySelectorAll(`[data-testid="${esc(tid)}"]`).length === 1) { parts.unshift(`[data-testid="${esc(tid)}"]`); return parts.join(" > "); }
    const tag = node.tagName.toLowerCase();
    const sibs = [...node.parentElement.children].filter((c) => c.tagName === node.tagName);
    parts.unshift(sibs.length > 1 ? `${tag}:nth-of-type(${sibs.indexOf(node) + 1})` : tag);
    node = node.parentElement;
  }
  parts.unshift("body");
  return parts.join(" > ");
}

export function snippetFor(el) {
  return (el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 80);
}

export function resolveAnchor(pin) {
  try { const el = document.querySelector(pin.anchor); if (el) return el; } catch { /* bad selector */ }
  if (pin.snippet && pin.tag) {
    for (const el of document.querySelectorAll(pin.tag)) if ((el.textContent || "").replace(/\s+/g, " ").includes(pin.snippet)) return el;
  }
  return null;
}

// Where the user tapped → {anchor, snippet, tag, x, y} (fractions inside the anchor's box).
export function describePoint(clientX, clientY, ignore) {
  let target = document.elementFromPoint(clientX, clientY);
  if (!target || (ignore && ignore.contains(target))) return null;
  const clamp = (v) => Math.min(1, Math.max(0, v));
  const m = mapFor(target);
  if (m) {
    const mr = m.c.getBoundingClientRect();
    const ll = m.map.containerPointToLatLng([clientX - mr.left, clientY - mr.top]);
    return { anchor: selectorFor(m.c), snippet: "", tag: m.c.tagName.toLowerCase(), x: clamp((clientX - mr.left) / mr.width), y: clamp((clientY - mr.top) / mr.height), lat: ll.lat, lng: ll.lng };
  }
  const el = blockFor(target);
  const r = el.getBoundingClientRect();
  if (!r.width || !r.height) return null;
  return { anchor: selectorFor(el), snippet: snippetFor(el), tag: el.tagName.toLowerCase(), x: clamp((clientX - r.left) / r.width), y: clamp((clientY - r.top) / r.height) };
}
